const MAKECODE_ORIGIN = 'https://makecode.microbit.org';
const MAKECODE_URL = `${MAKECODE_ORIGIN}/?controller=2&light=1`;
const REQUEST_TIMEOUT_MS = 60000;

const fingerprintSource = source => {
    let hash = 2166136261;
    for (let index = 0; index < source.length; index++) {
        hash ^= source.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
};

const createProject = (source, buildId) => {
    const fingerprint = fingerprintSource(source);
    const dependencies = {core: '*'};
    const usesBluetooth = /\bbluetooth\./.test(source);
    if (usesBluetooth) dependencies.bluetooth = '*';
    const projectConfig = {
        name: `Domi Scratch microbit ${fingerprint}-${buildId}`,
        dependencies,
        description: `Generated by Domi Scratch (${fingerprint}-${buildId})`,
        files: [
            'main.ts',
            'README.md'
        ],
        preferredEditor: 'tsprj'
    };
    if (usesBluetooth) {
        // USB flashing replaces the micro:bit's previous BLE bond. Compile the
        // classroom remote in MakeCode's open-link mode so the car can reconnect
        // immediately without asking the child to repeat a pairing ceremony.
        projectConfig.yotta = {
            config: {
                'microbit-dal': {
                    bluetooth: {
                        open: 1,
                        security_level: null,
                        whitelist: 0
                    }
                }
            }
        };
    }
    return {
        text: {
            'main.ts': source,
            'README.md': `Generated by Domi Scratch\nSource fingerprint: ${fingerprint}\nBuild ID: ${buildId}`,
            'pxt.json': JSON.stringify(projectConfig)
        }
    };
};

const normalizeHex = download => {
    if (download instanceof Uint8Array) return download;
    if (download instanceof ArrayBuffer) return new Uint8Array(download);
    if (Array.isArray(download)) return new Uint8Array(download);
    if (typeof download === 'string') return new TextEncoder().encode(download);
    throw new Error('MakeCode returned an invalid HEX file');
};

class MicroBitMakeCodeCompiler {
    constructor (windowObject = window, documentObject = document) {
        this.window = windowObject;
        this.document = documentObject;
        this.frame = null;
        this.readyPromise = null;
        this.readyResolve = null;
        this.editorReady = false;
        this.workspaceReady = false;
        this.pendingRequests = new Map();
        this.pendingCompile = null;
        this.queue = Promise.resolve();
        this.handleMessage = this.handleMessage.bind(this);
    }

    compile (source) {
        const compilePromise = this.queue.then(() => this.compileNow(source));
        this.queue = compilePromise.catch(() => {});
        return compilePromise;
    }

    async compileNow (source) {
        await this.ensureFrame();
        const buildId = fingerprintSource(this.createRequestId());
        await this.sendRequest('importproject', {
            project: createProject(source, buildId),
            filters: {}
        });
        // importproject can acknowledge before the TypeScript editor has applied
        // the imported main.ts. Opening JavaScript gives us a controller-level
        // synchronization point so compile cannot return the previous program.
        await this.sendRequest('switchjavascript', {});

        const compileId = this.createRequestId();
        const result = new Promise((resolve, reject) => {
            const timeout = this.window.setTimeout(() => {
                this.pendingCompile = null;
                reject(new Error('MakeCode compilation timed out'));
            }, REQUEST_TIMEOUT_MS);
            this.pendingCompile = {
                id: compileId,
                resolve: download => {
                    this.window.clearTimeout(timeout);
                    this.pendingCompile = null;
                    resolve(normalizeHex(download));
                },
                reject: error => {
                    this.window.clearTimeout(timeout);
                    this.pendingCompile = null;
                    reject(error instanceof Error ? error : new Error(String(error)));
                }
            };
        });
        this.postMessage({
            type: 'pxteditor',
            id: compileId,
            action: 'compile',
            response: true
        });
        return result;
    }

    ensureFrame () {
        if (this.readyPromise) return this.readyPromise;

        this.readyPromise = new Promise(resolve => {
            this.readyResolve = resolve;
        });
        this.window.addEventListener('message', this.handleMessage);

        this.frame = this.document.createElement('iframe');
        this.frame.tabIndex = -1;
        this.frame.setAttribute('aria-hidden', 'true');
        Object.assign(this.frame.style, {
            border: '0',
            height: '1px',
            opacity: '0',
            pointerEvents: 'none',
            position: 'fixed',
            width: '1px'
        });
        this.frame.src = MAKECODE_URL;
        this.document.body.appendChild(this.frame);
        return this.readyPromise;
    }

    handleMessage (event) {
        if (!this.frame || event.source !== this.frame.contentWindow) return;
        if (event.origin !== MAKECODE_ORIGIN) return;
        const message = event.data;
        if (!message || typeof message !== 'object') return;

        if (message.type === 'pxthost' && message.action === 'workspacesync') {
            this.postMessage({...message, projects: []});
            this.workspaceReady = true;
            this.resolveReady();
            return;
        }

        if (message.type === 'ready') {
            this.editorReady = true;
            this.resolveReady();
            return;
        }

        if (this.pendingCompile && (message.download || message.id === this.pendingCompile.id)) {
            const download = message.download || (message.resp && message.resp.download);
            if (download) {
                this.pendingCompile.resolve(download);
                return;
            }
            if (message.success === false || message.error) {
                this.pendingCompile.reject(new Error(message.error || 'MakeCode compilation failed'));
                return;
            }
        }

        const pendingRequest = this.pendingRequests.get(message.id);
        if (pendingRequest) {
            this.pendingRequests.delete(message.id);
            this.window.clearTimeout(pendingRequest.timeout);
            if (message.success === false || message.error) {
                pendingRequest.reject(new Error(message.error || 'MakeCode rejected the project'));
            } else {
                pendingRequest.resolve(message.resp);
            }
        }
    }

    sendRequest (action, body) {
        const id = this.createRequestId();
        return new Promise((resolve, reject) => {
            const timeout = this.window.setTimeout(() => {
                this.pendingRequests.delete(id);
                reject(new Error(`MakeCode ${action} timed out`));
            }, REQUEST_TIMEOUT_MS);
            this.pendingRequests.set(id, {resolve, reject, timeout});
            this.postMessage({
                type: 'pxteditor',
                id,
                action,
                response: true,
                ...body
            });
        });
    }

    resolveReady () {
        if (this.editorReady && this.workspaceReady && this.readyResolve) {
            this.readyResolve();
            this.readyResolve = null;
        }
    }

    postMessage (message) {
        this.frame.contentWindow.postMessage(message, MAKECODE_ORIGIN);
    }

    dispose () {
        if (this.window.removeEventListener) {
            this.window.removeEventListener('message', this.handleMessage);
        }
        if (this.frame && this.frame.remove) this.frame.remove();
        this.frame = null;
        this.readyPromise = null;
        this.readyResolve = null;
        this.editorReady = false;
        this.workspaceReady = false;
    }

    createRequestId () {
        return `domi-${Date.now()}-${Math.random()}`;
    }
}

const compileMicroBitSource = source => {
    const compiler = new MicroBitMakeCodeCompiler();
    return compiler.compile(source).finally(() => compiler.dispose());
};

export {
    MAKECODE_ORIGIN,
    MicroBitMakeCodeCompiler,
    compileMicroBitSource
};
